'use client'

import { useEffect, useRef } from 'react'

/*
 * ─── Wind ───
 * Fine specks carried sideways across the range, in slow gusts. Warm grey, tiny and
 * faint, denser low over the ridges than up in the sky. Pauses when the hero is off
 * screen or the tab is hidden, and draws nothing for people who prefer reduced motion.
 */
const COUNT = 90

export function Wind() {
  const ref = useRef(null)

  useEffect(() => {
    const canvas = ref.current
    if (!canvas) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const ctx = canvas.getContext('2d')
    let w = 0
    let h = 0
    let raf = 0
    let running = false

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      w = canvas.clientWidth
      h = canvas.clientHeight
      canvas.width = w * dpr
      canvas.height = h * dpr
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }

    let seed = 11
    const rnd = () => (seed = (seed * 16807) % 2147483647) / 2147483647
    // height as a fraction of the box, weighted towards the ridges
    const band = () => 0.25 + Math.pow(rnd(), 0.7) * 0.7
    const specks = Array.from({ length: COUNT }, () => ({
      x: rnd(),
      y: band(),
      r: 0.4 + rnd() * 0.7,
      speed: 0.6 + rnd() * 0.8, // relative to the wind
      sway: rnd() * Math.PI * 2,
      a: 0.12 + rnd() * 0.22,
    }))

    let t = 0
    let last = performance.now()
    const tick = (now) => {
      const dt = Math.min(now - last, 50) / 1000
      last = now
      t += dt
      // the wind: a steady breeze with a slow gust every ten seconds or so
      const gust = 1 + 0.9 * Math.pow(Math.max(0, Math.sin(t * 0.55)), 3)
      const wind = 0.018 * gust // fraction of the width per second
      ctx.clearRect(0, 0, w, h)
      ctx.fillStyle = '#7d776e'
      for (const p of specks) {
        p.x += wind * p.speed * dt
        p.sway += dt * (0.8 + p.speed)
        if (p.x > 1.02) {
          p.x = -0.02
          p.y = band()
        }
        const y = (p.y + Math.sin(p.sway) * 0.006) * h
        // fade in and out at the edges of the box
        const edge = Math.min(1, p.x / 0.08, (1 - p.x) / 0.08)
        ctx.globalAlpha = p.a * Math.max(0, edge)
        ctx.beginPath()
        ctx.arc(p.x * w, y, p.r, 0, Math.PI * 2)
        ctx.fill()
      }
      raf = requestAnimationFrame(tick)
    }

    const start = () => {
      if (running) return
      running = true
      last = performance.now()
      raf = requestAnimationFrame(tick)
    }
    const stop = () => {
      running = false
      cancelAnimationFrame(raf)
    }

    resize()
    const ro = new ResizeObserver(resize)
    ro.observe(canvas)
    const io = new IntersectionObserver(([e]) => (e.isIntersecting ? start() : stop()))
    io.observe(canvas)
    const onVis = () => (document.hidden ? stop() : start())
    document.addEventListener('visibilitychange', onVis)
    return () => {
      stop()
      ro.disconnect()
      io.disconnect()
      document.removeEventListener('visibilitychange', onVis)
    }
  }, [])

  return <canvas ref={ref} className="h-wind" aria-hidden="true" />
}
